'use client';

import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'motion/react';
import { ICONS } from '@/lib/iconRegistry';
import GlobalTitle from '@shared/GlobalTitle';
import SectionShapes from '@shared/SectionShapes';
import { TECH_ICONS } from '@/lib/techIconMap';

const HEADER_H = 64;
const STICKY_START = 96;

// ── Single tech icon box ──────────────────────────────────────────────────────
function TechBox({ item }) {
    const IconComponent = TECH_ICONS[item.name] ?? null;

    const initials = item.name
        .split(/[\s.]/)
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

    const Wrapper = item.href ? Link : 'div';
    const wrapperProps = item.href
        ? { href: item.href, title: `${item.name} development services`, className: 'flex flex-col items-center gap-1.5 group cursor-pointer' }
        : { className: 'flex flex-col items-center gap-1.5 group cursor-default' };

    return (
        <Wrapper {...wrapperProps}>
            <div className="w-[52px] h-[52px] sm:w-[60px] sm:h-[60px] border border-primary/[0.08] rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md group-hover:shadow-primary/20 group-hover:border-primary/30 group-hover:from-primary/[0.12] group-hover:to-primary/[0.06] transition-all duration-300 shrink-0">
                {IconComponent ? (
                    <IconComponent size={32} className="w-7 h-7 sm:w-8 sm:h-8 object-contain" />
                ) : item.icon ? (
                    <Image
                        src={item.icon}
                        alt={item.name}
                        width={32}
                        height={32}
                        className="w-7 h-7 sm:w-8 sm:h-8 object-contain"
                    />
                ) : (
                    <span className="text-primary text-xs font-bold tracking-wide">
                        {initials}
                    </span>
                )}
            </div>
            <span className={`text-[10px] text-center leading-tight max-w-[62px] ${item.href ? 'text-primary font-semibold group-hover:underline' : 'text-gray-600'}`}>
                {item.name}
            </span>
        </Wrapper>
    );
}

function CategoryCard({ category, index, colIndex, stackGroups }) {
    const Icon = ICONS[category.icon];
    const mobileTop = STICKY_START + index * HEADER_H;
    const desktopTop = STICKY_START + colIndex * HEADER_H;

    return (
        <div
            id={category.id}
            className="scroll-mt-28 sticky top-[var(--stack-top-mobile)] md:top-[var(--stack-top-desktop)] z-[var(--stack-z-mobile)] md:z-[var(--stack-z-desktop)] rounded-2xl bg-white border border-gray-100 shadow-md"
            style={{
                '--stack-top-mobile': `${mobileTop}px`,
                '--stack-top-desktop': `${desktopTop}px`,
                '--stack-z-mobile': `${index + 1}`,
                '--stack-z-desktop': `${colIndex + 1}`,
            }}
        >
            <div className="h-16 flex items-center gap-3 px-4 sm:px-6 border-b border-gray-100">
                <div className="w-9 h-9 shrink-0 rounded-lg bg-[#2d3561] flex items-center justify-center">
                    {Icon && <Icon size={18} className="text-white" />}
                </div>
                <div>
                    <p className="font-bold text-gray-900 leading-tight">
                        {category.href ? (
                            <Link href={category.href} className="hover:text-primary transition-colors">
                                {category.title}
                            </Link>
                        ) : category.title}
                    </p>
                    <div className="h-[2px] w-full bg-primary rounded-full mt-0.5" />
                </div>
            </div>

            <div className={`p-4 sm:p-6 grid gap-6 ${category.groups.length > 1 ? `grid-cols-1 ${stackGroups ? 'sm:grid-cols-1' : 'sm:grid-cols-2'}` : 'grid-cols-1'}`}>
                {category.groups.map((group) => (
                    <div key={group.label}>
                        <p className="text-[11px] text-gray-400 uppercase tracking-widest font-semibold mb-4">
                            {group.label}
                        </p>
                        <div className="flex flex-wrap gap-3">
                            {group.items.map((item) => (
                                <TechBox key={item.name} item={item} />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function TechnologiesServices({ data, stackGroups = false }) {
    if (!data) return null;

    return (
        <section className="relative bg-[#f7f8fc] py-12 md:py-20">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <SectionShapes variant="dark" />
                <div className="absolute top-0 right-0 w-[500px] h-[400px] bg-primary/[0.04] rounded-full blur-[120px]" />
                <div className="absolute bottom-0 left-0 w-[400px] h-[300px] bg-primary/[0.03] rounded-full blur-[100px]" />
            </div>
            <div className="relative z-10 max-w-[1440px] mx-auto px-6">
                <motion.div
                    className="text-center mb-8"
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                >
                    <GlobalTitle
                        topText={data.topText}
                        titleText={data.titleText}
                        coloredText={data.coloredText}
                        classes="text-center"
                        topTextClass="justify-center"
                    />
                    {data.description && (
                        <p className="mt-4 text-muted-foreground max-w-4xl mx-auto">
                            {data.description}
                        </p>
                    )}
                </motion.div>

                {/* ── Stacking cards ── */}
                <div className="relative grid grid-cols-1 md:grid-cols-2 gap-4">
                    {data.categories.map((category, i) => (
                        <CategoryCard
                            key={category.id}
                            category={category}
                            index={i}
                            colIndex={Math.floor(i / 2)}
                            stackGroups={stackGroups}
                        />
                    ))}
                </div>

            </div>
        </section>
    );
}
